import { useState, useEffect, useCallback } from "react";
import { getDashboardStats, getGearConflicts, getGearShortageForecast, seedData } from "@/lib/api";
import { toast } from "sonner";

// Get Sun-Sat week boundaries for a given date
export function getWeekRange(date) {
  const d = new Date(date);
  const day = d.getDay(); // 0=Sun
  const sun = new Date(d);
  sun.setDate(d.getDate() - day);
  const sat = new Date(sun);
  sat.setDate(sun.getDate() + 6);
  return {
    start: sun.toISOString().split('T')[0],
    end: sat.toISOString().split('T')[0]
  };
}

export function getMonthRange(date) {
  const d = new Date(date);
  const start = new Date(d.getFullYear(), d.getMonth(), 1);
  const end = new Date(d.getFullYear(), d.getMonth() + 1, 0);
  return {
    start: start.toISOString().split('T')[0],
    end: end.toISOString().split('T')[0]
  };
}

export function getYearRange(date) {
  const y = new Date(date).getFullYear();
  return { start: `${y}-01-01`, end: `${y}-12-31` };
}

export function formatDateShort(dateStr) {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

// Fill in all months between start and end with 0 for missing months
export function padMonthlyRevenue(monthlyObj, startDate, endDate) {
  const result = [];
  const start = new Date(startDate + 'T00:00:00');
  const end = new Date(endDate + 'T00:00:00');
  let current = new Date(start.getFullYear(), start.getMonth(), 1);
  const last = new Date(end.getFullYear(), end.getMonth(), 1);
  while (current <= last) {
    const key = `${current.getFullYear()}-${String(current.getMonth() + 1).padStart(2, '0')}`;
    const label = current.toLocaleDateString('en-US', { month: 'short' });
    result.push({ month: label, revenue: monthlyObj[key] || 0, key });
    current.setMonth(current.getMonth() + 1);
  }
  return result;
}

export function useDashboardData(role) {
  const [stats, setStats] = useState(null);
  const [conflicts, setConflicts] = useState([]);
  const [shortages, setShortages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const isCrew = role === "crew";

  const [yearRange] = useState(() => getYearRange(new Date()));
  const [dateStart, setDateStart] = useState(yearRange.start);
  const [dateEnd, setDateEnd] = useState(yearRange.end);

  const fetchData = useCallback(async () => {
    try {
      // Crew get a personal stats payload; conflict/shortage endpoints
      // reject crew logins.
      const statsRes = await getDashboardStats(dateStart, dateEnd);
      setStats(statsRes.data);
      if (!isCrew) {
        const [conflictsRes, shortageRes] = await Promise.all([
          getGearConflicts(),
          getGearShortageForecast(30)
        ]);
        setConflicts(conflictsRes.data);
        setShortages(shortageRes.data?.shortages || []);
      }
    } catch {
      toast.error("Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  }, [dateStart, dateEnd, isCrew]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSeed = async () => {
    setSeeding(true);
    try {
      await seedData();
      toast.success("Demo data loaded");
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Seed failed");
    } finally {
      setSeeding(false);
    }
  };

  return { stats, conflicts, shortages, loading, seeding, dateStart, dateEnd, setDateStart, setDateEnd, fetchData, handleSeed };
}
